import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Users, ChevronLeft, Settings } from "lucide-react";
import { CreateProfileDrawer } from "@/components/profile/CreateProfileDrawer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSmartDashboard } from "@/hooks/useSmartDashboard";
import { Badge } from "@/components/ui/badge";

export const CreateSection = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();
  const { smartData, isLoading } = useSmartDashboard();

  const handleCreateOwnTimeline = () => {
    navigate('/timeline');
  };

  const handleCreateForSomeoneElse = () => {
    setIsCreateModalOpen(true);
  };

  const handleManagedTimelines = () => {
    navigate('/friends?tab=managed');
  };

  const handleProfileCreated = (username: string) => {
    navigate(`/timeline/${username}`);
  };

  // Determine smart CTA content
  const getTimelineCTA = () => {
    if (isLoading) return { title: t('dashboard:cards.timeline.loading'), subtitle: t('dashboard:cards.timeline.loadingSubtitle'), icon: Clock };
    
    if (!smartData.hasTimeline || smartData.needsTimelineSetup) {
      return {
        title: t('dashboard:cards.timeline.setupTitle'),
        subtitle: t('dashboard:cards.timeline.setupSubtitle'),
        icon: Clock
      };
    }
    
    if (smartData.hasTimelineSections) {
      return {
        title: t('dashboard:cards.timeline.viewTitle'),
        subtitle: smartData.hasTimelineEntries 
          ? t('dashboard:cards.timeline.continueSubtitle')
          : t('dashboard:cards.timeline.firstMemorySubtitle'),
        icon: Clock
      };
    }

    return {
      title: t('dashboard:cards.timeline.completeTitle'),
      subtitle: t('dashboard:cards.timeline.completeSubtitle'),
      icon: Clock
    };
  };

  const getManagedProfilesCTA = () => {
    if (smartData.managedProfilesCount > 0) {
      return {
        title: t('dashboard:cards.managed.manageTitle'),
        subtitle: t('dashboard:cards.managed.managedCount', { count: smartData.managedProfilesCount }),
        icon: Settings,
        onClick: handleManagedTimelines,
        showBadge: true,
        badgeCount: smartData.managedProfilesCount
      };
    }
    
    return {
      title: t('dashboard:cards.managed.createTitle'),
      subtitle: t('dashboard:cards.managed.createSubtitle'),
      icon: Users,
      onClick: handleCreateForSomeoneElse,
      showBadge: false
    };
  };

  const timelineCTA = getTimelineCTA();
  const managedCTA = getManagedProfilesCTA();

  return (
    <>
      <div className="space-y-4 mb-6">
        <Card 
          className="uplivion-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={handleCreateOwnTimeline}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-uplivion-blue/10">
                  <timelineCTA.icon className="w-6 h-6 text-uplivion-blue" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{timelineCTA.title}</h3>
                  <p className="text-muted-foreground text-sm">{timelineCTA.subtitle}</p>
                </div>
              </div>
              <ChevronLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="uplivion-card cursor-pointer hover:shadow-md transition-shadow"
          onClick={managedCTA.onClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-uplivion-blue/10">
                  <managedCTA.icon className="w-6 h-6 text-uplivion-blue" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{managedCTA.title}</h3>
                    {managedCTA.showBadge && (
                      <Badge variant="secondary" className="text-xs">
                        {managedCTA.badgeCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm">{managedCTA.subtitle}</p>
                </div>
              </div>
              <ChevronLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </div>
          </CardContent>
        </Card>
      </div>

      <CreateProfileDrawer
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultRelationshipType="gift"
        onSuccess={handleProfileCreated}
      />
    </>
  );
};